import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner */}
      {!showPreferences && (
        <div
          className="fixed bottom-4 left-4 z-[9999] max-w-sm bg-neutral-900 rounded-lg shadow-xl border border-neutral-700 p-4 animate-in slide-in-from-bottom-5 fade-in duration-300"
          role="dialog"
          aria-label={t('cookie.title', 'Cookie consent')}
        >
          <p className="text-sm text-neutral-300 mb-3 pr-6">
            {t('cookie.message')}{' '}
            <a
              href="/it/cookie-policy"
              className="font-medium underline hover:opacity-80 transition-opacity text-brand hover:text-brand-dark"
            >
              {t('cookie.learnMore')}
            </a>
          </p>
          <div className="flex gap-2">
            <button
              onClick={acceptAll}
              className="flex-1 py-2 px-3 text-sm font-semibold rounded-md border-none cursor-pointer transition-colors bg-brand text-neutral-900 hover:bg-brand-dark"
            >
              {t('cookie.acceptAll', 'Accetta tutti')}
            </button>
            <button
              onClick={rejectAll}
              className="flex-1 py-2 px-3 text-sm font-semibold rounded-md border-2 cursor-pointer transition-colors bg-transparent border-neutral-500 text-neutral-300 hover:bg-neutral-800"
            >
              {t('cookie.rejectAll', 'Rifiuta')}
            </button>
          </div>
          <button
            onClick={() => setShowPreferences(true)}
            className="mt-3 flex items-center justify-center gap-1.5 w-full text-xs text-neutral-400 hover:text-neutral-200 transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            {t('cookie.customize', 'Personalizza')}
          </button>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4 animate-in fade-in duration-200"
          onClick={() => setShowPreferences(false)}
        >
          <div
            className="bg-neutral-900 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 border border-neutral-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  {t('cookie.preferencesTitle', 'Preferenze Cookie')}
                </h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 text-neutral-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-neutral-400 mb-6">
                {t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.')}
              </p>

              <div className="space-y-3">
                {/* Necessary */}
                <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-white">{t('cookie.necessary', 'Necessari')}</p>
                    <p className="text-xs text-neutral-400">{t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}</p>
                  </div>
                  <span className="text-xs text-brand font-medium">{t('cookie.alwaysOn', 'Sempre attivi')}</span>
                </div>

                {/* Analytics */}
                <label className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg cursor-pointer hover:bg-neutral-700 transition-colors">
                  <div>
                    <p className="font-medium text-sm text-white">{t('cookie.analytics', 'Analitici')}</p>
                    <p className="text-xs text-neutral-400">{t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={tempPrefs.analytics}
                    onChange={(e) => setTempPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    className="h-5 w-5 rounded border-neutral-600 cursor-pointer accent-brand"
                  />
                </label>

                {/* Marketing */}
                <label className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg cursor-pointer hover:bg-neutral-700 transition-colors">
                  <div>
                    <p className="font-medium text-sm text-white">{t('cookie.marketing', 'Marketing')}</p>
                    <p className="text-xs text-neutral-400">{t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={tempPrefs.marketing}
                    onChange={(e) => setTempPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    className="h-5 w-5 rounded border-neutral-600 cursor-pointer accent-brand"
                  />
                </label>
              </div>

              <div className="mt-6 flex gap-2 justify-end">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="py-2 px-4 text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                >
                  {t('cookie.cancel', 'Annulla')}
                </button>
                <button
                  onClick={() => {
                    savePreferences(tempPrefs)
                    setShowPreferences(false)
                  }}
                  className="py-2 px-4 text-sm font-semibold rounded-md cursor-pointer transition-colors bg-brand text-neutral-900 hover:bg-brand-dark"
                >
                  {t('cookie.savePreferences', 'Salva preferenze')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
